// One voice for auth errors. Raw Supabase messages are developer-speak; these are
// written to be read by the person standing at the door, in the house voice:
// lowercase, quiet, kind, please where something is asked, never an em-dash.

/** What a person is told when the network, not their typing, was the problem.
 *  Nothing they entered was wrong, so the line says so and asks for nothing but
 *  another go. */
export const NETWORK_LINE = "we couldn't reach innernet just now. please check your connection and try again."

/** The code /auth/callback puts in ?error= when it could not reach the auth
 *  server (a stable code, never a sentence, so no raw text rides the address bar). */
export const NETWORK_CODE = 'network_unreachable'

/** The plain apology for anything we have not read and deliberately worded. */
export const SOMETHING_WRONG = 'something went wrong signing you in. please try again.'

/** Whether a message is the sound of a connection failing: the browser's own
 *  words (Chrome "Failed to fetch", Safari "Load failed", Firefox "NetworkError
 *  when attempting to fetch"), Node's, and the SDK's retryable-fetch error. */
export function isNetworkError(raw: string | null | undefined): boolean {
  if (!raw) return false
  return /failed to fetch|fetch failed|load failed|networkerror|network error|network request failed|err_network|err_internet|internet connection|ECONN|ENOTFOUND|EAI_AGAIN|socket hang up|timed out|timeout|AuthRetryableFetchError|you are offline|\boffline\b/i.test(raw)
}

/** For an error that was THROWN, not returned (a rejected fetch, a missing key, a
 *  bug): it is never printed. The connection failing gets its own line; anything
 *  else gets the plain apology. `String(err)` used to go straight to the screen,
 *  which is how "TypeError: Failed to fetch" reached the sign-in page. */
export function friendlyThrown(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  if (offline || isNetworkError(raw)) return NETWORK_LINE
  return SOMETHING_WRONG
}

export function friendlyAuthError(raw: string | null | undefined): string {
  if (!raw) return ''

  // the connection, before anything else: a message about the network is never
  // about the person's email, password or link
  if (raw === NETWORK_CODE || isNetworkError(raw)) return NETWORK_LINE

  // Codes minted by /auth/callback (see authCode there) — the callback never
  // forwards a raw SDK sentence any more, so these are the whole vocabulary of
  // a bounced sign-in.
  if (raw === 'browser_switched')
    return 'that sign-in finished in a different browser than it started in, so it could not be completed here. please open innernet.live in safari or chrome and try again.'
  if (raw === 'link_expired') return 'that link has expired. please request a new one.'
  if (raw === 'link_used') return 'that link was already used. please request a new one.'

  // the auth server's own cooldown between two emails to one address ("For security
  // purposes, you can only request this after 52 seconds"). Not the person's doing,
  // and not a limit they have hit for the day: they only need to wait a moment.
  if (/for security purposes|only request this/i.test(raw)) return 'please wait a moment, then ask for another email.'

  if (/rate limit/i.test(raw))
    return 'too many attempts for now. email is rate-limited for a little while, so please wait a few minutes and try again.'
  if (/invalid login credentials/i.test(raw))
    return "that email and password don't match. please try again, or reset your password below."
  if (raw === 'auth_callback_failed') return 'that link expired or was already used. please request a new one.'
  if (/expired/i.test(raw)) return 'that link has expired. please request a new one.'
  if (/otp|token/i.test(raw)) return 'that link is no longer valid. please request a new one.'
  if (/email not confirmed/i.test(raw)) return 'please confirm your email first. check your inbox for the confirmation link.'

  // ⚠️ THE FALLBACK IS NOT `return raw`. It was, and that is how a Supabase
  // message telling US to "use @supabase/ssr on both the server and client"
  // was printed to a person trying to sign in. A message we have not read and
  // deliberately worded is not fit to show: anything carrying developer
  // vocabulary is replaced, and only human-sounding text passes through.
  if (looksLikeDeveloperText(raw)) return SOMETHING_WRONG
  // a sentence we let through still speaks the house voice: its first letter is
  // lowercased ("User already registered" → "user already registered"), unless it
  // opens on an initialism ("OTP …"), which stays as it is.
  return raw.replace(/^([A-Z])(?=[a-z])/, (c) => c.toLowerCase())
}

/** Markers of a message written for a developer, not for the person at the door. */
function looksLikeDeveloperText(raw: string): boolean {
  return (
    /@supabase|supabase\.|SSR|SvelteKit|Next\.js|localStorage|sessionStorage|storage was cleared|code[_ ]verifier|stack trace|undefined is not|\bnull\b|\bAPI key\b|500|ECONN|fetch failed/i.test(
      raw,
    ) || raw.length > 180
  )
}
